import React from 'react';
import { connect } from 'react-redux';
import { NavLink, withRouter } from 'react-router-dom';

import { getNavigationData } from 'utils/api.service';
import {
  updateMenuCategoryID,
  updateAvailableCategories,
  updateUserInfo,
} from 'store/actions/app_actions';
import { updateUserRole } from 'store/actions/hosting_actions';
import { toggleMenuCollapsed } from 'store/actions/menu_actions';

import MenuDropdown from 'components/MenuDropdown';
import schema from './schema';
import autodeskLogo from 'assets/images/icons/autodesk-logo.svg';

class Menu extends React.Component {
  state = {
    drawers: {},
  };

  constructor() {
    super();

    this.toggleDrawer = this.toggleDrawer.bind(this);
    this.handleToggleMenu = this.handleToggleMenu.bind(this);
  }

  handleToggleMenu() {
    this.props.toggleMenuCollapsed();
  }

  handleDocumentationClick = (e) => {
    e.preventDefault();
    // Collapse the menu if it's expanded
    if (!this.props.isCollapsed) {
      this.props.toggleMenuCollapsed();
    }
    // Navigate to documentation using React Router
    this.props.history.push('/documentation');
  };

  componentDidMount() {
    // Navigation data is already loaded by PrivateRoute via Redux
    // Use Redux state instead of making duplicate API call
    console.log('📋 [MENU] Using navigation data from Redux store');
    this.syncNavigationDataFromRedux();
  }

  componentDidUpdate(prevProps) {
    // Update navigation data when Redux state changes
    if (prevProps.categories !== this.props.categories) {
      console.log('📋 [MENU] Redux categories updated, syncing...');
      this.syncNavigationDataFromRedux();
    }
  }

  syncNavigationDataFromRedux = () => {
    if (this.props.categories && this.props.categories.projects) {
      console.log('✅ [MENU] Navigation data available from Redux');
      this.setState({ navigation: this.props.categories });
      // Update user info if available
      if (this.props.categories.userinfo) {
        this.props.updateUserInfo(this.props.categories.userinfo);
      }
    } else {
      console.log('⚠️ [MENU] Navigation data not available in Redux, will wait');
    }
  }

  toggleDrawer(drawer) {
    this.setState((state) => ({
      drawers: { ...state.drawers, [drawer]: !state.drawers[drawer] },
    }));
  }

  setSelectedCategory(service, id) {
    const hostingWorkstream = this.props.categories.projects.find(
      (p) => p.org === service
    );
    let categoryName;

    if (hostingWorkstream) {
      categoryName = hostingWorkstream.workstream.find(
        (w) => w.workstreamname === id
      ).displayname;

      this.props.updateUserRole(hostingWorkstream.role);
    } else {
      const service = this.props.categories.thservices.find(
        (s) => s.servicename === service
      );

      if (service) {
        const category = service.categories.find((c) => c.categoryid === id);

        categoryName = category.categoryname;
      }
    }

    this.props.updateMenuCategoryID(service, id, categoryName);
  }

  renderMenuCategories(items = [], baseUrl, serviceName) {
    return items.map((item) => {
      const categoryIdentifier = item.categoryname || item.workstreamname;
      const parsedCategoryName = categoryIdentifier
        .toLowerCase()
        .split(' ')
        .join('_');
      const categoryName =
        serviceName === 'testdata'
          ? item.categoryname
          : item.categoryid || item.workstreamname;
      const itemProps = {
        id: item.categoryid || item.id,
        to: `${baseUrl}/${parsedCategoryName}`,
        onClick: () => this.setSelectedCategory(serviceName, categoryName),
      };
      let navItem = (
        <NavLink {...itemProps}>
          <span>{item.categoryname || item.displayname}</span>
        </NavLink>
      );

      if (item.status === 'disabled') {
        navItem = navItem = (
          <span className="disabled">
            {item.categoryname || item.displayname}
          </span>
        );
      }

      return <li key={itemProps.id}>{navItem}</li>;
    });
  }

  renderDropdowns(categories) {
    return schema.map((service, index) => {
      // hosting has a different structure
      if (service.servicename === 'hosting') {
        const hostedServices = categories[service.accessor];

        if (hostedServices) {
          const items = hostedServices.map((hostedService) => {
            const dropdownProps = {
              items: this.renderMenuCategories(
                hostedService[service.children],
                service.baseUrl,
                hostedService.name
              ),
              title: hostedService.displayName,
            };

            return (
              <MenuDropdown
                key={hostedService.servicename}
                {...dropdownProps}
              />
            );
          });
          const dropdownProps = {
            items,
            title: service.displayName,
          };

          if (items.length) {
            return (
              <MenuDropdown key={service.servicename} {...dropdownProps} />
            );
          }
        }

        return null;
      } else {
        if (service.accessor && categories[service.accessor]) {
          const category = categories[service.accessor].find(
            (c) => c.servicename === service.servicename
          );
          const subcategories = category[service.children];
          const dropdownProps = {
            items: this.renderMenuCategories(
              subcategories,
              service.baseUrl,
              service.servicename
            ),
            title: service.displayName,
          };

          return <MenuDropdown key={service.servicename} {...dropdownProps} />;
        }

        // Add separator before Dashboard - only show if user has projects
        if (service.servicename === 'dashboard') {
          const hasProjects = categories.projects && categories.projects.length > 0;
          if (hasProjects) {
            return (
              <React.Fragment key={service.servicename}>
                <div className="menu-separator"></div>
                <NavLink to={service.baseUrl} className="menu-item">
                  <span>{service.displayName}</span>
                </NavLink>
              </React.Fragment>
            );
          }
          return null;
        }

        return (
          <NavLink to={service.baseUrl} key={service.servicename} className="menu-item">
            <span>{service.displayName}</span>
          </NavLink>
        );
      }
    });
  }

  render() {
    const navigation = this.renderDropdowns(this.props.categories);
    const { isCollapsed } = this.props;

    return (
      <nav className={`menu ${isCollapsed ? 'collapsed' : ''}`}>
        <NavLink className="menu-brand" to="/">
          <img
            className="logo"
            src="https://testing-hub-static-assets.s3.amazonaws.com/images/ADSK_LOGO_NEW.png"
            alt="Autodesk"
          />
        </NavLink>

        {!isCollapsed && (
          <>
            <div className="menu-section">{navigation}</div>

            <div className="menu-links">
              {/* <NavLink to="/dashboard-v2">Dashboard V2</NavLink> */}
              <NavLink to="/jobsqueue">View jobs queue</NavLink>
            </div>
          </>
        )}

        {!isCollapsed && (
          <div className="menu-documentation">
            <a href="/documentation" onClick={this.handleDocumentationClick}>Documentation</a>
          </div>
        )}

        {isCollapsed && (
          <div className="menu-collapsed-content">
            <NavLink to="/" className="autodesk-logo-icon" title="Autodesk">
              <img
                src={autodeskLogo}
                alt="Autodesk"
                width="28"
                height="28"
              />
            </NavLink>
            <NavLink to="/dashboard" className="dashboard-icon" title="Dashboard">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="rgba(255, 255, 255, 0.8)" />
              </svg>
            </NavLink>
            <NavLink to="/dashboard-v2" className="dashboard-v2-icon" title="Dashboard V2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm7 0h3v3h-3v-3zm0 4h3v2h-3v-2zm4-4h2v6h-2v-6z" fill="rgba(255, 255, 255, 0.8)" />
              </svg>
            </NavLink>
            <NavLink to="/jobsqueue" className="jobs-queue-icon" title="View jobs queue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 2C7.45 2 7 2.45 7 3V5H5C3.9 5 3 5.9 3 7V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V7C21 5.9 20.1 5 19 5H17V3C17 2.45 16.55 2 16 2H8ZM8 4H16V5H8V4ZM5 7H19V19H5V7ZM7 9V11H17V9H7ZM7 13V15H17V13H7Z" fill="rgba(255, 255, 255, 0.8)" />
              </svg>
            </NavLink>
            <a href="/documentation" onClick={this.handleDocumentationClick} className="documentation-icon" title="Documentation">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" fill="rgba(255, 255, 255, 0.8)" />
              </svg>
            </a>
          </div>
        )}

        <button
          className="menu-toggle-btn"
          onClick={this.handleToggleMenu}
          aria-label={isCollapsed ? 'Open menu' : 'Close menu'}
        >
          <span className={`arrow ${isCollapsed ? 'right' : 'left'}`}></span>
        </button>
      </nav>
    );
  }
}

const mapStateToProps = (state) => ({
  categories: state.menu.categories,
  isCollapsed: state.menu.isCollapsed,
  user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
  toggleMenuCollapsed: () => dispatch(toggleMenuCollapsed()),
  updateUserRole: (role) => dispatch(updateUserRole(role)),
  updateUserInfo: (userInfo) => dispatch(updateUserInfo(userInfo)),
  updateAvailableCategories: (categories) =>
    dispatch(updateAvailableCategories(categories)),
  updateMenuCategoryID: (service, id, categoryName) =>
    dispatch(updateMenuCategoryID(service, id, categoryName)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Menu));
